// Purpose: Hook with auth action methods (login, signup, logout, resend verification) for Supabase users.

import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/components/ui/use-toast";
import { cleanupAuthState } from '@/utils/authUtils';
import { trackGoal } from '@/lib/analytics/track-goal';
import type { Profile } from '@/types/database.types';

// Re-export Profile type for convenience
export type { Profile } from '@/types/database.types';

export const useAuthActions = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      cleanupAuthState();

      try { await supabase.auth.signOut({ scope: 'global' }); } catch {}
      const { error, data } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        if (error.message.includes('Email not confirmed')) {
          toast({
            title: "Email not confirmed",
            description: "Please confirm your email address. Check your inbox or request a new confirmation email.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Login failed",
            description: error.message || "Please check your credentials and try again.",
            variant: "destructive",
          });
        }
        throw error;
      }

      trackGoal('login', { method: 'email' });
      toast({
        title: "Login successful",
        description: "Welcome back!",
      });
    } catch (error: any) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (name: string, email: string, password: string) => {
    try {
      setIsLoading(true);
      cleanupAuthState();
      try { await supabase.auth.signOut({ scope: 'global' }); } catch {}

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: name } }
      });

      if (error) throw error;

      trackGoal('signup', { method: 'email' });
      toast({
        title: "Account created",
        description: "Check your email to confirm your registration.",
      });
    } catch (error: any) {
      toast({
        title: "Registration failed",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      cleanupAuthState();
      await supabase.auth.signOut({ scope: 'global' });
      toast({ title: "Signed out", description: "You have been successfully signed out." });
      window.location.href = '/login';
    } catch (error: any) {
      toast({
        title: "Logout error",
        description: error.message || "An error occurred while signing out.",
        variant: "destructive",
      });
    }
  };

  const resendVerificationEmail = async (email: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.resend({ type: 'signup', email });
      if (error) throw error;
      toast({
        title: "Email sent",
        description: "A new confirmation email has been sent to your email address.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Could not send confirmation email.",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const sendMagicLink = async (email: string) => {
    try {
      setIsLoading(true);
      cleanupAuthState();
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`,
        },
      });
      if (error) throw error;
      toast({
        title: "Magic link sent",
        description: "Check your email for a login link.",
      });
    } catch (error: any) {
      toast({
        title: "Failed to send magic link",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    login,
    signup,
    logout,
    resendVerificationEmail,
    sendMagicLink,
    isLoading
  };
};
